import { useEffect, useRef, useState } from "react";
import {
  Mail,
  Lock,
  User,
  Phone,
  MapPin,
  Eye,
  EyeOff,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const PcCareRegister = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const otpSectionRef = useRef(null);

  const validateField = (name, value) => {
    const normalized = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        return normalized ? "" : "Name is required";
      case "phone":
        if (!normalized) return "Mobile number is required";
        return /^[6-9]\d{9}$/.test(normalized)
          ? ""
          : "Enter valid 10-digit mobile";
      case "email":
        if (!normalized) return "Email is required";
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
          ? ""
          : "Enter valid email";
      case "password":
        return value.length >= 6 ? "" : "Password must be at least 6 characters";
      default:
        return "";
    }
  };

  const validateAll = () => {
    const nextErrors = {};
    Object.keys(form).forEach((key) => {
      if (key === "address") return;
      const msg = validateField(key, form[key]);
      if (msg) nextErrors[key] = msg;
    });
    if (!accepted) nextErrors.accepted = "Please accept Terms & Conditions";
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, phone: digitsOnly }));
      setError("");
      setSuccess("");
      setFieldErrors((prev) => ({ ...prev, phone: validateField("phone", digitsOnly) }));
      return;
    }

    const nextForm = { ...form, [name]: value };
    setForm(nextForm);
    setError("");
    setSuccess("");

    const msg = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, [name]: msg }));

    if (name === "email") {
      setOtpSent(false);
      setOtpVerified(false);
    }
  };

  const normalizeBaseUrl = (rawUrl) =>
    (rawUrl || "")
      .trim()
      .replace(/([^:]\/)\/+/g, "$1")
      .replace(/\/+$/, "");

  const getBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const backendRoot = process.env.REACT_APP_BACKEND;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    return normalizeBaseUrl(
      backendUrl || apiBase || backendRoot || "http://localhost:8000"
    );
  };

  const extractErrorMessage = (data, fallback) => {
    const messageText =
      typeof data?.message === "string" ? data.message.trim() : "";
    const errorText = typeof data?.error === "string" ? data.error.trim() : "";
    const messageLower = messageText.toLowerCase();
    const isGenericMessage =
      messageText &&
      (messageLower.includes("registration failed") ||
        messageLower.includes("register error") ||
        messageLower === "error");

    if (isGenericMessage && errorText) return errorText;
    if (typeof data === "string" && data.trim()) return data;
    if (Array.isArray(data?.message) && data.message.length) {
      return data.message.join(", ");
    }
    if (messageText) {
      return messageText;
    }
    if (Array.isArray(data?.errors) && data.errors.length) {
      return data.errors.join(", ");
    }
    if (errorText) {
      return errorText;
    }
    return fallback;
  };

  const goToOtpVerify = (payload) => {
    navigate("/verify-otp", { state: payload });
  };

  const handleCreateAccount = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    setOtpLoading(false);

    try {
      const baseUrl = getBaseUrl();
      const finalEmail = form.email.trim().toLowerCase();
      const finalMobile = form.phone.trim();

      const res = await fetch(`${baseUrl}/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: finalEmail,
          mobile: finalMobile,
          address: form.address.trim() || null,
          password: form.password,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const serverMsg = extractErrorMessage(data, "").trim();

        if (
          res.status === 400 &&
          (!serverMsg ||
            serverMsg.toLowerCase().includes("registration failed") ||
            serverMsg.toLowerCase().includes("user register"))
        ) {
          throw new Error(
            "Email or mobile already exists. Please use different details."
          );
        }

        if (res.status === 500 && !serverMsg) {
          throw new Error("Server error while creating account. Please try again.");
        }

        throw new Error(serverMsg || `Registration failed (${res.status})`);
      }

      const otpFromResponse = data?.otp || data?.data?.otp || "";
      setOtpSent(true);
      setOtpVerified(false);

      if (data?.otp_sent) {
        setSuccess("Account created. OTP sent to your email.");
      } else if (otpFromResponse) {
        setSuccess(
          "Account created. Email OTP failed on server; OTP is available from backend response."
        );
      } else {
        setSuccess(
          "Account created but OTP email was not sent. Contact support if OTP is not received."
        );
      }

      goToOtpVerify({
        email: finalEmail,
        password: form.password,
        message: data?.otp_sent
          ? "OTP sent to your email."
          : "OTP email failed. Use OTP shown in the form.",
      });
      return true;
    } catch (err) {
      const msg = err?.message?.includes("Failed to fetch")
        ? "Server not reachable. Please try again."
        : err?.message || "Registration failed";

      if (
        msg.toLowerCase().includes("email already exists") ||
        msg.toLowerCase().includes("already exists")
      ) {
        const finalEmail = form.email.trim().toLowerCase();
        goToOtpVerify({
          email: finalEmail,
          password: form.password,
          message: "Email already exists. Enter OTP to verify your account.",
        });
      }

      if (msg.toLowerCase().includes("mobile already exists")) {
        setFieldErrors((prev) => ({
          ...prev,
          phone: "This mobile number is already registered",
        }));
      }

      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (loading || otpLoading) return;

    setError("");
    setSuccess("");

    if (!validateAll()) return;
    if (!otpSent) {
      await handleCreateAccount();
      return;
    }
    if (!otpVerified) {
      goToOtpVerify({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        message: "Enter OTP to verify your account.",
      });
      return;
    }
  };

  useEffect(() => {
    if (otpSent && otpSectionRef.current) {
      otpSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [otpSent]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl grid grid-cols-1 md:grid-cols-2 overflow-hidden">
        {/* ================= LEFT VIDEO SECTION ================= */}
        <div className="hidden md:block relative">
          <video
            src="/ani.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>

        {/* ================= RIGHT REGISTER SECTION ================= */}
        <div className="p-6 sm:p-12 flex flex-col justify-center bg-white">
          <h2 className="text-3xl font-bold text-center text-[#1F3C88] mb-4">
            Create Account
          </h2>

          <form onSubmit={handleRegister} className="space-y-4">
            {/* Name */}
            <div>
              <label className="text-sm text-gray-600">Full Name</label>
              <div className="relative mt-1">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  placeholder=""
                  value={form.name}
                  onChange={handleChange}
                  disabled={otpSent}
                  className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
                />
              </div>
              {fieldErrors.name && <p className="text-xs text-red-500 mt-1">{fieldErrors.name}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="text-sm text-gray-600">Mobile Number</label>
              <div className="relative mt-1">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  placeholder=""
                  value={form.phone}
                  onChange={handleChange}
                  disabled={otpSent}
                  className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
                />
              </div>
              {fieldErrors.phone && <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="text-sm text-gray-600">Email address</label>
              <div className="relative mt-1">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  placeholder=""
                  value={form.email}
                  onChange={handleChange}
                  disabled={otpSent}
                  className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
                />
              </div>
              {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
            </div>

            {/* Address */}
            <div>
              <label className="text-sm text-gray-600">Address</label>
              <div className="relative mt-1">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="address"
                  placeholder=""
                  value={form.address}
                  onChange={handleChange}
                  disabled={otpSent}
                  className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-sm text-gray-600">Password</label>
              <div className="relative mt-1">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder=""
                  value={form.password}
                  onChange={handleChange}
                  disabled={otpSent}
                  className="w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#1F3C88]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>}
            </div>

            {/* Terms */}
            <div className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={accepted}
                onChange={() => { setAccepted(!accepted); setFieldErrors((p) => ({ ...p, accepted: "" })); }}
                className="accent-[#1F3C88]"
              />
              <span>
                I agree to the {" "}
                <Link
                  to="/terms-conditions"
                  className="text-[#1F3C88] font-medium"
                >
                  Terms & Conditions
                </Link>
              </span>
            </div>
            {fieldErrors.accepted && <p className="text-xs text-red-500">{fieldErrors.accepted}</p>}

            {/* Error */}
            {error && (
              <div className="text-red-500 text-sm text-center">
                {error}
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="text-green-600 text-sm text-center">
                {success}
              </div>
            )}

            {/* Submit */}
            <div className="flex flex-col items-center gap-3">
              <button
                type="submit"
                disabled={loading || otpLoading || (otpSent && !otpVerified)}
                className="px-8 py-2 bg-[#1F3C88] hover:bg-[#16306e] text-white text-sm font-medium rounded-full shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-60"
              >
                {loading
                  ? "Creating..."
                  : otpSent && !otpVerified
                  ? "Verify OTP Below"
                  : "Create Account"}
              </button>
              {!otpSent && (
                <button
                  type="button"
                  onClick={() => {
                    const candidateEmail = form.email.trim().toLowerCase();
                    if (!candidateEmail) {
                      setError("Enter email first to verify an existing account.");
                      return;
                    }
                    setError("");
                    setOtpSent(true);
                  }}
                  className="text-xs text-[#1F3C88] hover:underline"
                >
                  Already registered? Verify account with OTP
                </button>
              )}
            </div>
          </form>

          {otpSent && !otpVerified && (
            <div
              ref={otpSectionRef}
              className="mt-6 rounded-2xl border border-[#e6e9ff] bg-[#f7f9ff] p-4 text-sm text-gray-700"
            >
              OTP verification moved to a separate page. Please use the button above.
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-xl text-gray-500 mt-2">
            Already have an account?{" "}
            <Link to="/login" className="text-[#1F3C88] font-semibold hover:underline">
              Login
            </Link>
          </p>

          <p className="text-center text-xs text-gray-400 mt-2">
            <Link to="/contact" className="hover:underline">
              Need Help? Contect Now
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PcCareRegister;
